import { classNames } from '@ui-components-3/ui'
import { OperatingHoursBar } from 'hooks/operating-hours/types'
import { useMemo } from 'react'
import { roundToNearestMinutes } from 'date-fns'
import { formatTimeToNearestFiveMinutes } from 'hooks/operating-hours/utils'
import { useTranslation } from 'react-i18next'

type WeekDayCardProps = {
  data: OperatingHoursBar
}

const WeekDayCard = ({ data }: WeekDayCardProps) => {
  const { title, segments, isHighlighted } = data || {}

  const { t } = useTranslation()

  const formattedTimes = useMemo(
    () =>
      segments?.map(({ startsAt, endsAt }) => {
        const is24Hours =
          !!startsAt &&
          !!endsAt &&
          roundToNearestMinutes(new Date(endsAt)).valueOf() - roundToNearestMinutes(new Date(startsAt)).valueOf() ===
            1000 * 60 * 60 * 24

        return is24Hours
          ? t('serviceHours.modal.24Hours')
          : [startsAt, endsAt].map(formatTimeToNearestFiveMinutes).filter(Boolean).join(' - ')
      }) || [],
    [segments, t],
  )

  const accessibilityLabel = useMemo(() => `${title}: ${formattedTimes.join(', ')}`, [formattedTimes, title])

  if (!data) return null

  return (
    <li
      className={classNames(
        'border-l-4',
        isHighlighted ? 'border-l-primary-600 bg-primary-100' : 'border-l-transparent',
      )}
      aria-label={accessibilityLabel}
    >
      <div className="px-4 py-2 sm:px-6 sm:py-3" aria-hidden="true">
        <div className="typography-body mb-0.5 font-semibold text-neutral-800 sm:mb-1">{title}</div>
        <div className="flex flex-col gap-1">
          {formattedTimes?.map((item, segmentIndex) => (
            <p key={String(segmentIndex)} className="typography-body-l text-neutral-800">
              {item}
            </p>
          ))}
        </div>
      </div>
    </li>
  )
}

export default WeekDayCard
